/**
 * "Did you mean maria@gmail.com?" (owner, 2026-10-12: catch email typos; a mistyped address can never confirm).
 *
 * Self-contained (no imports) so the signup script and its tests load it the same way. Only the common personal
 * providers our older, US audience uses are suggested, and only for near misses, so a real company domain that merely
 * resembles one is left alone.
 */

/** Personal mail domains, most common first. A domain on this list is never "corrected". */
const KNOWN_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'comcast.net', 'msn.com',
  'live.com', 'me.com', 'att.net', 'verizon.net', 'sbcglobal.net', 'bellsouth.net', 'cox.net', 'charter.net',
  'earthlink.net', 'ymail.com', 'rocketmail.com', 'mac.com', 'googlemail.com', 'protonmail.com', 'proton.me',
  'optonline.net', 'frontier.com', 'windstream.net', 'roadrunner.com', 'juno.com', 'netzero.net', 'mail.com',
] as const

/** Providers that only live at .com: "gmail.co" or "yahoo.cm" is a typo, never a different company. */
const COM_ONLY = new Set(['gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'msn', 'live', 'ymail', 'googlemail'])

const KNOWN = new Set<string>(KNOWN_DOMAINS)

/** Short slips the distance rule can't safely catch: "gma.com" is two letters from gmail.com but only 7 long. */
const SHORT_SLIPS: Record<string, string> = {
  'gma.com': 'gmail.com', 'gml.com': 'gmail.com', 'gmai.co': 'gmail.com', 'gmal.co': 'gmail.com',
  'yah.com': 'yahoo.com', 'yaho.co': 'yahoo.com',
}

/** Optimal string alignment distance (Levenshtein plus one swap of neighbours: "gamil" -> "gmail" is 1). */
function distance(a: string, b: string): number {
  const rows = a.length + 1, cols = b.length + 1
  const d: number[][] = Array.from({ length: rows }, (_, i) => Array.from({ length: cols }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)))
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
    }
  }
  return d[a.length][b.length]
}

/** The corrected domain for a near miss of a known provider, or null when the domain looks deliberate. */
export function suggestDomain(rawDomain: string): string | null {
  const domain = rawDomain.trim().toLowerCase().replace(/\.+$/, '')
  if (!domain || KNOWN.has(domain)) return null
  if (SHORT_SLIPS[domain]) return SHORT_SLIPS[domain]
  // A missing dot ("gmailcom") or a typo in the ending of a .com-only provider ("gmail.con", "yahoo.co").
  for (const name of COM_ONLY) {
    if (domain === `${name}com`) return `${name}.com`
    const [label, ...rest] = domain.split('.')
    if (label === name && rest.length === 1 && rest[0] !== 'com' && distance(rest[0], 'com') <= 2) return `${name}.com`
  }
  let best: string | null = null, bestDistance = Infinity
  for (const known of KNOWN_DOMAINS) {
    const d = distance(domain, known)
    if (d < bestDistance) { best = known; bestDistance = d }
  }
  // One slip anywhere; two only in a longer address, where two slips can't turn one real domain into another.
  const allowed = domain.length >= 9 ? 2 : 1
  return best && bestDistance <= allowed ? best : null
}

/** "maria@gmial.com" -> "maria@gmail.com"; null when the address looks right or isn't an address. */
export function suggestEmail(rawEmail: string): string | null {
  const email = rawEmail.trim()
  const at = email.lastIndexOf('@')
  if (at < 1 || at === email.length - 1) return null
  const domain = suggestDomain(email.slice(at + 1))
  return domain ? `${email.slice(0, at)}@${domain}` : null
}

/** The provider label analytics may carry for a suggestion (never the address or the person's domain). */
export function suggestionLabel(suggestedEmail: string): string {
  const domain = suggestedEmail.slice(suggestedEmail.lastIndexOf('@') + 1)
  return KNOWN.has(domain) ? domain : 'other'
}
